import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearch } from '@components/reducers/search';
import TableContainer from '@mui/material/TableContainer';
import { FormatDate } from '@components/Shared/Utils';
import { GetAccountSettings } from '@themes/common/data';
import useCustomerReducer from '../useCustomerReducer';
import { AccountPaperContainer } from '../components';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate, useLocation } from 'react-router-dom';
import { DisplayPrice } from '@components/Shared/Utils/price';
import { DataGrid, } from '@mui/x-data-grid';
import { CleanupFilterItems } from '@commons/filters/frontend';
import { parseISO } from 'date-fns';
import { Typography } from '@mui/material';
const translations = GetAccountSettings().translations;
const { customers_order_order_number, customers_order_date, customers_order_marking, customers_account_payment_terms, customers_products_total, customers_products_ammount_to_pay, customers_invoice_paid, } = translations;
const Invoices = (_) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [{ customer }] = useCustomerReducer();
    const [sort, setSort] = useState([]);
    const [filters, setFilters] = useState({
        items: [],
    });
    const params = useMemo(() => {
        return { customerId: customer === null || customer === void 0 ? void 0 : customer.id };
    }, [customer === null || customer === void 0 ? void 0 : customer.id]);
    const [state, { setPageAndLimit, setExtraFilters }] = useSearch(`${process.env.HOST}/api/invoices`, 'customer_invoices', params);
    const pagination = useMemo(() => ({
        page: state.filters.page - 1,
        pageSize: state.filters.limit,
    }), [state.filters.limit, state.filters.page]);
    const handlePaginationModelChange = useCallback((e) => {
        setPageAndLimit(e.page + 1, e.pageSize);
    }, [setPageAndLimit]);
    const handleRowClick = useCallback((params) => {
        navigate(`${location.pathname}?id=${params.row.id}`);
    }, [location.pathname, navigate]);
    const columns = useMemo(() => [
        {
            field: 'businessContactOrder.orderNumber',
            headerName: customers_order_order_number,
            valueGetter: (params) => { var _a; return (_a = params.row.businessContactOrder) === null || _a === void 0 ? void 0 : _a.orderNumber; },
            width: 150,
        },
        {
            field: 'ledger.invoiceDate',
            headerName: customers_order_date,
            type: 'date',
            renderCell: (params) => { var _a; return FormatDate((_a = params.row.ledger) === null || _a === void 0 ? void 0 : _a.invoiceDate, 'yyyy-MM-dd'); },
            width: 120,
            valueGetter: (params) => { var _a, _b; return ((_a = params.row.ledger) === null || _a === void 0 ? void 0 : _a.invoiceDate) ? parseISO((_b = params.row.ledger) === null || _b === void 0 ? void 0 : _b.invoiceDate.toString()) : null; },
        },
        {
            field: 'businessContactOrder.businessContactOrderNumber',
            headerName: customers_order_marking,
            width: 200,
            valueGetter: (params) => { var _a; return (_a = params.row.businessContactOrder) === null || _a === void 0 ? void 0 : _a.businessContactOrderNumber; },
        },
        {
            field: 'paymentTerm.description',
            headerName: customers_account_payment_terms,
            width: 350,
            valueGetter: (params) => { var _a; return (_a = params.row.paymentTerm) === null || _a === void 0 ? void 0 : _a.description; },
        },
        {
            field: 'ledger.invoiceAmount',
            headerName: customers_products_total,
            type: 'number',
            valueGetter: (params) => { var _a, _b; return (_b = (_a = params.row.ledger) === null || _a === void 0 ? void 0 : _a.invoiceAmount) !== null && _b !== void 0 ? _b : 0; },
            renderCell: (params) => { var _a, _b; return DisplayPrice((_b = (_a = params.row.ledger) === null || _a === void 0 ? void 0 : _a.invoiceAmount) !== null && _b !== void 0 ? _b : 0); },
            width: 150,
        },
        {
            field: 'ledger.restAmount',
            headerName: customers_products_ammount_to_pay,
            type: 'number',
            valueGetter: (params) => { var _a, _b; return !parseFloat(((_b = (_a = params.row.ledger) === null || _a === void 0 ? void 0 : _a.restAmount) === null || _b === void 0 ? void 0 : _b.toString()) || '0'); },
            renderCell: (params) => {
                var _a, _b, _c, _d;
                const paid = !parseFloat(((_b = (_a = params.row.ledger) === null || _a === void 0 ? void 0 : _a.restAmount) === null || _b === void 0 ? void 0 : _b.toString()) || '0');
                return (_jsx(Typography, Object.assign({ sx: { color: paid ? '#2ecc71' : undefined } }, { children: paid ? customers_invoice_paid : DisplayPrice((_d = (_c = params.row.ledger) === null || _c === void 0 ? void 0 : _c.restAmount) !== null && _d !== void 0 ? _d : 0) })));
            },
            width: 150,
        },
        {
            field: 'id',
            headerName: '',
            width: 50,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (_jsx(ChevronRightIcon, { sx: { cursor: 'pointer' }, onClick: () => {
                    navigate(`${location.pathname}?id=${params.row.id}`);
                } })),
        },
    ], [location.pathname, navigate]);
    useEffect(() => {
        setExtraFilters({
            sort,
            filters: CleanupFilterItems(filters, {
                date: ['ledger.invoiceDate'],
                string: ['businessContactOrder.orderNumber', 'businessContactOrder.businessContactOrderNumber', 'paymentTerm.description'],
                number: ['ledger.invoiceAmount', 'ledger.restAmount'],
            }),
        });
    }, [filters, setExtraFilters, sort]);
    return (_jsx(TableContainer, Object.assign({ component: AccountPaperContainer }, { children: _jsx(DataGrid, { columns: columns, rows: state.items, initialState: {
                pagination: {
                    paginationModel: { page: 0, pageSize: 25 },
                },
            }, pageSizeOptions: [10, 25, 50, 100, 250], sortModel: sort, filterModel: filters, onSortModelChange: setSort, onFilterModelChange: setFilters, onRowClick: handleRowClick, paginationModel: pagination, onPaginationModelChange: handlePaginationModelChange, loading: state.loading }) })));
};
export default Invoices;
